import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import React from 'react';
import { createRoot } from 'react-dom/client';
import { SalesInvoiceTemplateRenderer } from '../../modules/sales/components/SalesInvoiceTemplateRenderer';
import { CustomDocumentRenderer } from '../components/CustomDocumentRenderer';

/**
 * Capture an existing HTML element in the DOM and convert it to a jsPDF document.
 * Matches exact styles, colors, fonts, tables, and layouts.
 */
export async function generatePdfFromHtmlElement(element, options = {}) {
  if (!element) {
    throw new Error('Element not provided for PDF generation');
  }

  const {
    templateKey = 'standard',
    paperType = 'a4',
    filename = 'document',
    save = true,
    scale = 2,
  } = options;

  const isThermal = templateKey === 'thermal' || paperType === 'thermal';
  const isA5 = templateKey === 'a5' || templateKey === 'a5_preprinted' || paperType === 'a5';

  // High-resolution canvas capture with html2canvas
  const canvas = await html2canvas(element, {
    scale: scale,
    useCORS: true,
    allowTaint: true,
    logging: false,
    backgroundColor: '#ffffff',
    scrollY: 0,
    scrollX: 0,
    windowWidth: element.scrollWidth,
  });

  const imgData = canvas.toDataURL('image/jpeg', 0.96);
  const canvasWidth = canvas.width;
  const canvasHeight = canvas.height;

  let doc;
  const safeFilename = `${(filename || 'document').replace(/\s+/g, '-')}.pdf`.replace(/[^a-zA-Z0-9._-]/g, '_');

  if (isThermal) {
    // Continuous thermal roll receipt (80mm)
    const pdfWidth = 80;
    const pdfHeight = Math.max(30, (canvasHeight * pdfWidth) / canvasWidth);
    doc = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: [pdfWidth, pdfHeight],
    });
    doc.addImage(imgData, 'JPEG', 0, 0, pdfWidth, pdfHeight, '', 'FAST');
  } else {
    // Sheet paper (A4 or A5)
    const pdfWidth = isA5 ? 148 : 210;
    const pdfPageHeight = isA5 ? 210 : 297;
    const totalHeightInMm = (canvasHeight * pdfWidth) / canvasWidth;

    doc = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: isA5 ? 'a5' : 'a4',
    });

    if (totalHeightInMm <= pdfPageHeight + 2) {
      // Single Page
      doc.addImage(imgData, 'JPEG', 0, 0, pdfWidth, totalHeightInMm, '', 'FAST');
    } else {
      // Multi-Page Slicing
      let heightLeft = totalHeightInMm;
      let position = 0;
      doc.addImage(imgData, 'JPEG', 0, position, pdfWidth, totalHeightInMm, '', 'FAST');
      heightLeft -= pdfPageHeight;

      while (heightLeft > 0) {
        position -= pdfPageHeight;
        doc.addPage();
        doc.addImage(imgData, 'JPEG', 0, position, pdfWidth, totalHeightInMm, '', 'FAST');
        heightLeft -= pdfPageHeight;
      }
    }
  }

  if (save) {
    doc.save(safeFilename);
    return undefined;
  }

  return doc.output('arraybuffer');
}

/**
 * Headless offscreen render of template component into PDF.
 * Used when sharing or downloading from list views or background handlers.
 */
export async function renderTemplateOffscreenAndGeneratePdf(config = {}) {
  const {
    docType = 'invoice', // 'invoice' | 'quotation'
    order,
    quotation,
    company,
    templateKey: propTemplateKey,
    showTax = true,
    currency,
    itemFormat,
    dateFormat,
    filename,
    save = true,
  } = config;

  const dataDoc = docType === 'quotation' ? quotation : order;
  const defaultTemplate = docType === 'quotation'
    ? (company?.quotationPrintTemplate || 'standard')
    : (company?.salesPrintTemplate || 'custom');
  const templateKey = propTemplateKey || defaultTemplate;

  const isThermal = templateKey === 'thermal';
  const isA5 = templateKey === 'a5' || templateKey === 'a5_preprinted' || company?.printPaperType === 'a5';

  if (typeof document === 'undefined') {
    throw new Error('Offscreen PDF generation is only supported in browser environment');
  }

  // Create temporary offscreen container
  const container = document.createElement('div');
  container.style.position = 'fixed';
  container.style.left = '-9999px';
  container.style.top = '0';
  container.style.width = isThermal ? '420px' : (isA5 ? '650px' : '900px');
  container.style.maxWidth = '100%';
  container.style.background = '#ffffff';
  container.style.boxSizing = 'border-box';
  container.style.zIndex = '-9999';
  container.className = 'headless-pdf-capture';
  document.body.appendChild(container);

  const root = createRoot(container);

  try {
    if (templateKey === 'custom') {
      root.render(
        <div style={{ background: '#ffffff', padding: '16px' }}>
          <CustomDocumentRenderer
            docType={docType}
            order={order}
            quotation={quotation}
            company={company}
            designSettings={company?.invoiceDesignSettings}
            currency={currency || company?.currency || 'USD'}
            dateFormat={dateFormat || company?.dateFormat || 'dd/MM/yyyy'}
            itemFormat={itemFormat || company?.printItemNameFormat || 'name_sku'}
          />
        </div>
      );
    } else {
      // Use SalesInvoiceTemplateRenderer for standard & numbered templates
      const renderDoc = docType === 'quotation' ? {
        ...quotation,
        invoiceNumber: quotation?.quotationNumber,
        invoiceDate: quotation?.quotationDate,
        salesType: 'Quotation',
        status: quotation?.status,
        docType: 'quotation',
      } : order;

      root.render(
        <div style={{ background: '#ffffff', padding: '16px' }}>
          <SalesInvoiceTemplateRenderer
            order={renderDoc}
            company={company}
            templateKey={templateKey}
            currency={currency || company?.currency || 'USD'}
            itemFormat={itemFormat || company?.printItemNameFormat || 'name_sku'}
            dateFormat={dateFormat || company?.dateFormat || 'dd/MM/yyyy'}
            showTax={showTax}
          />
        </div>
      );
    }

    // Wait for DOM layout and potential barcode/font render
    await new Promise((resolve) => setTimeout(resolve, 250));

    const result = await generatePdfFromHtmlElement(container, {
      templateKey,
      paperType: isThermal ? 'thermal' : (isA5 ? 'a5' : 'a4'),
      filename: filename || (dataDoc?.invoiceNumber || dataDoc?.quotationNumber || dataDoc?.orderNumber || 'document'),
      save,
      scale: 2,
    });

    return result;
  } finally {
    // Cleanup DOM
    try {
      root.unmount();
      container.remove();
    } catch {
      // Ignore unmount error
    }
  }
}

/**
 * Universal PDF Web Share handler.
 * Uses Web Share API with fallback to download if share is not available or cancelled.
 */
export async function sharePdfArrayBuffer(arrayBuffer, { filename = 'document', title = 'Document', text = '' } = {}) {
  if (!arrayBuffer) return;
  const safeFilename = `${(filename || 'document').replace(/\s+/g, '-')}.pdf`.replace(/[^a-zA-Z0-9._-]/g, '_');
  const blob = new Blob([arrayBuffer], { type: 'application/pdf' });

  let file = null;
  try {
    file = new File([blob], safeFilename, { type: 'application/pdf' });
  } catch (e) {
    // Ignore File constructor error on older mobile browsers
  }

  const canShareFiles =
    typeof navigator !== 'undefined' &&
    !!navigator.share &&
    !!navigator.canShare &&
    !!file &&
    navigator.canShare({ files: [file] });

  if (canShareFiles) {
    try {
      await navigator.share({
        title,
        text,
        files: [file],
      });
      return;
    } catch (err) {
      if (err?.name === 'AbortError') return;
      // Fall through to download
    }
  }

  // Fallback: Trigger browser download or open in browser
  try {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = safeFilename;
    document.body.appendChild(link);
    link.click();
    setTimeout(() => {
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }, 1000);
  } catch (e) {
    console.error('Download fallback failed:', e);
  }
}
